import React from 'react';
import { useSelector } from 'react-redux';
import { Field, useFormState } from 'react-final-form';
import { Alert, Radio } from 'antd';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { VISIBILITY, VISIBILITY_VALUES, visibilityOf } from '../../util/postingVisibilityValues';

import css from './PostingVisibilityField.module.css';

/** The company setting's choices, as the options of a select, each saving one of the three values. */
export const JobRfpVisibilityOptions = () => {
  const intl = useIntl();
  return (
    <>
      {VISIBILITY_VALUES.map(value => (
        <option key={value} value={value}>
          {intl.formatMessage({ id: `PostingVisibility.${value}` })}
        </option>
      ))}
    </>
  );
};

/** The words for a stored visibility, whichever spelling it was saved in. */
export const PostingVisibilityLabel = ({ value }) => <FormattedMessage id={`PostingVisibility.${visibilityOf(value)}`} />;

// The company's own setting, from the signed-in member's company listing, when it is loaded.
const useCompanyVisibility = () =>
  useSelector(state => {
    const onBoardingInfo = state.user?.profileListing?.attributes?.publicData?.onBoardingInfo;
    return onBoardingInfo && 'jobRfpVisibility' in onBoardingInfo ? visibilityOf(onBoardingInfo.jobRfpVisibility) : null;
  });

/**
 * Who a job or RFP is open to, on its last step: everyone, or only the company's preferred network.
 * An existing posting starts from its own value, a new one from the company's setting; when that is
 * not known, nothing is chosen and the server applies the company's setting. Switching a live posting
 * to restricted says that people who already have its link can still open it.
 */
const PostingVisibilityField = ({ isLive }) => {
  const companyValue = useCompanyVisibility();
  const { initialValues } = useFormState({ subscription: { initialValues: true } });
  const ownValue = initialValues && initialValues.visibility;
  return (
    <Field name="visibility" initialValue={ownValue === undefined && companyValue ? companyValue : undefined}>
      {({ input, meta }) => (
        <div className={css.root}>
          <div className={css.title}>
            <FormattedMessage id="PostingVisibilityField.title" />
          </div>
          <Radio.Group value={input.value || undefined} onChange={e => input.onChange(e.target.value)}>
            {VISIBILITY_VALUES.map(value => (
              <Radio key={value} value={value}>
                <FormattedMessage id={`PostingVisibility.${value}`} />
              </Radio>
            ))}
          </Radio.Group>
          {input.value ? null : (
            <p className={css.hint}>
              <FormattedMessage id="PostingVisibilityField.companyDefault" />
            </p>
          )}
          {isLive && meta.initial === VISIBILITY.OPEN && input.value && input.value !== VISIBILITY.OPEN ? (
            <Alert className={css.notice} type="info" showIcon message={<FormattedMessage id="PostingVisibilityField.switchNotice" />} />
          ) : null}
        </div>
      )}
    </Field>
  );
};

export default PostingVisibilityField;
